/**
 * Smooth Sales wishlist (app-free).
 *
 * Stores saved products in localStorage and exposes two custom elements:
 * - <wishlist-button>  heart toggle rendered on product cards / product pages
 * - <wishlist-grid>    renders the saved list on the wishlist page
 *
 * The grid fetches each saved product from /products/<handle>.js at render
 * time, so prices and stock are never stale (deleted products are pruned,
 * failures fall back to the snapshot captured at save time). It also renders
 * a read-only shared view when the URL carries ?share=<handles>, which is
 * what "Share via email" links to. Cart adds dispatch Shopify's standard
 * CartLinesUpdateEvent so the drawer/icon/totals stay in sync.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'smooth-sales:wishlist:v1';
const CHANGE_EVENT = 'wishlist:change';
const SHARE_PARAM = 'share';
const SHARE_LIMIT = 24;

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

// Until the theme is published the wishlist template reaches the page via
// ?view=wishlist (admin can only assign published-theme templates), so
// links back to this page must carry the view param along.
function withViewParam(pathname) {
  const view = new URLSearchParams(window.location.search).get('view');
  return view ? `${pathname}?view=${encodeURIComponent(view)}` : pathname;
}

const wishlistStore = {
  read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  },

  write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* storage full or blocked — fail quietly */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
  },

  has(handle) {
    return this.read().some((item) => item.handle === handle);
  },

  toggle(item) {
    const items = this.read();
    const index = items.findIndex((existing) => existing.handle === item.handle);
    if (index >= 0) {
      items.splice(index, 1);
    } else {
      items.unshift(item);
    }
    this.write(items);
    return index < 0;
  },

  remove(handle) {
    this.write(this.read().filter((item) => item.handle !== handle));
  },
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', this.#onToggle);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #onToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    wishlistStore.toggle({
      handle: this.dataset.handle,
      id: this.dataset.id,
      title: this.dataset.title,
      url: this.dataset.url,
      price: this.dataset.price,
      image: this.dataset.image,
    });
  };

  #sync = () => {
    const saved = wishlistStore.has(this.dataset.handle);
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    this.button.setAttribute('aria-label', saved ? this.dataset.labelRemove : this.dataset.labelAdd);
    this.toggleAttribute('saved', saved);
  };
}

/* ---------- wishlist page ---------- */

const HEART_OUTLINE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19.5 12.6 12 20l-7.5-7.4A5 5 0 1 1 12 6.1a5 5 0 1 1 7.5 6.5Z"></path></svg>';
const HEART_FILLED =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M19.5 12.6 12 20l-7.5-7.4A5 5 0 1 1 12 6.1a5 5 0 1 1 7.5 6.5Z"></path></svg>';

function formatMoney(cents) {
  return `$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function resizeImage(url, size) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(size));
    return parsed.toString();
  } catch (error) {
    return url;
  }
}

/* Mirrors the markup of snippets/price.liquid (card size) so wishlist
 * prices wear the same red tag as everywhere else. Compare-at renders as a
 * plain span — never struck through (competitor pricing, ACCC rules).
 * Sold out renders "Sold Out" on the tag instead of a price. */
function buildPrice(product, soldOut = false) {
  if (soldOut) {
    const wrap = document.createElement('div');
    wrap.className = 'ss-price';
    const tag = document.createElement('span');
    tag.className = 'ss-price__tag';
    const accessible = document.createElement('span');
    accessible.className = 'visually-hidden';
    accessible.textContent = 'Sold out';
    const label = document.createElement('span');
    label.className = 'ss-price__amount ss-price__amount--soldout';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = 'Sold Out';
    tag.append(accessible, label);
    wrap.appendChild(tag);
    return wrap;
  }

  const onSale = typeof product.compare_at_price === 'number' && product.compare_at_price > product.price;

  const wrap = document.createElement('div');
  wrap.className = onSale ? 'ss-price ss-price--sale' : 'ss-price';

  if (onSale) {
    const dontPay = document.createElement('span');
    dontPay.className = 'ss-price__dont-pay';
    const label = document.createElement('span');
    label.setAttribute('aria-hidden', 'true');
    label.textContent = "DON'T PAY ";
    const compare = document.createElement('span');
    compare.className = 'ss-price__compare';
    compare.textContent = formatMoney(product.compare_at_price);
    dontPay.append(label, compare);
    wrap.appendChild(dontPay);
  }

  const tag = document.createElement('span');
  tag.className = 'ss-price__tag';

  const accessible = document.createElement('span');
  accessible.className = 'visually-hidden';
  accessible.textContent = formatMoney(product.price);

  const amount = document.createElement('span');
  amount.className = 'ss-price__amount';
  amount.setAttribute('aria-hidden', 'true');
  const [dollars, cents] = (product.price / 100).toFixed(2).split('.');
  amount.append(`$${Number(dollars).toLocaleString('en-AU')}`);
  const sup = document.createElement('sup');
  sup.className = 'ss-price__cents';
  sup.textContent = cents;
  amount.appendChild(sup);

  tag.append(accessible, amount);
  wrap.appendChild(tag);

  if (onSale) {
    const save = document.createElement('span');
    save.className = 'ss-price__save';
    save.textContent = `SAVE ${formatMoney(product.compare_at_price - product.price)}`;
    wrap.appendChild(save);
  }

  return wrap;
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.sharedHandles = this.#readShareParam();
    this.#load();
    if (!this.sharedHandles) {
      document.addEventListener(CHANGE_EVENT, this.#onChange);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  /* Re-render on store changes (removals elsewhere on the page) without
   * refetching what we already have. */
  #onChange = () => {
    if (!this.products) return;
    const saved = new Set(wishlistStore.read().map((item) => item.handle));
    this.products = this.products.filter((product) => saved.has(product.handle));
    this.#renderList();
  };

  #readShareParam() {
    const value = new URLSearchParams(window.location.search).get(SHARE_PARAM);
    if (!value) return null;
    const handles = value
      .split(',')
      .map((handle) => decodeURIComponent(handle.trim()))
      .filter((handle) => /^[a-z0-9-]+$/i.test(handle))
      .slice(0, SHARE_LIMIT);
    return handles.length > 0 ? handles : null;
  }

  async #load() {
    const stored = wishlistStore.read();
    const handles = this.sharedHandles || stored.map((item) => item.handle);

    if (handles.length === 0) {
      this.#renderEmpty();
      return;
    }

    this.#renderSkeleton(handles.length);

    const results = await Promise.allSettled(
      handles.map(async (handle) => {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (response.status === 404) return { handle, deleted: true };
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return { handle, product: await response.json() };
      })
    );

    const products = [];
    const deleted = new Set();
    for (const [index, result] of results.entries()) {
      const handle = handles[index];
      if (result.status === 'fulfilled' && result.value.product) {
        products.push({ ...result.value.product, handle });
      } else if (result.status === 'fulfilled' && result.value.deleted) {
        deleted.add(handle);
      } else {
        /* network hiccup — fall back to the snapshot captured at save time */
        const snapshot = stored.find((item) => item.handle === handle);
        if (snapshot) products.push({ snapshotOnly: true, ...snapshot });
      }
    }

    if (deleted.size > 0 && !this.sharedHandles) {
      wishlistStore.write(wishlistStore.read().filter((item) => !deleted.has(item.handle)));
    }

    this.products = products;
    if (!this.isConnected) return;
    this.#renderList();
  }

  #renderSkeleton(count) {
    const list = document.createElement('ul');
    list.className = 'wishlist-grid__list';
    list.setAttribute('role', 'list');
    for (let i = 0; i < Math.min(count, 8); i += 1) {
      const item = document.createElement('li');
      item.className = 'wishlist-grid__skeleton';
      item.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
      list.appendChild(item);
    }
    this.replaceChildren(list);
  }

  #renderEmpty() {
    const empty = document.createElement('div');
    empty.className = 'wishlist-grid__empty';

    const heart = document.createElement('span');
    heart.className = 'wishlist-grid__empty-heart';
    heart.innerHTML = HEART_OUTLINE;

    const title = document.createElement('h2');
    title.className = 'wishlist-grid__empty-title';
    title.textContent = this.sharedHandles ? 'This shared list is empty' : 'Your wish list is empty';

    const text = document.createElement('p');
    text.textContent = this.dataset.emptyText || 'Nothing saved yet.';

    const cta = document.createElement('a');
    cta.className = 'button';
    cta.href = this.dataset.emptyLink || '/collections/all';
    cta.textContent = this.dataset.emptyCta || 'Start shopping';

    empty.append(heart, title, text, cta);
    this.replaceChildren(empty);
  }

  #renderList() {
    if (!this.products || this.products.length === 0) {
      this.#renderEmpty();
      return;
    }

    const fragment = document.createDocumentFragment();

    if (this.sharedHandles) {
      const banner = document.createElement('div');
      banner.className = 'wishlist-grid__banner';
      const note = document.createElement('span');
      note.textContent = "You're viewing a wish list that was shared with you. Tap a heart to save an item to your own list.";
      const mine = document.createElement('a');
      mine.href = withViewParam(window.location.pathname);
      mine.textContent = 'View my wish list';
      banner.append(note, mine);
      fragment.appendChild(banner);
    } else {
      fragment.appendChild(this.#buildBar());
    }

    const list = document.createElement('ul');
    list.className = 'wishlist-grid__list';
    list.setAttribute('role', 'list');
    for (const product of this.products) {
      list.appendChild(this.#buildCard(product));
    }
    fragment.appendChild(list);

    this.replaceChildren(fragment);
  }

  #buildBar() {
    const bar = document.createElement('div');
    bar.className = 'wishlist-grid__bar';

    const count = document.createElement('span');
    count.className = 'wishlist-grid__count';
    count.setAttribute('aria-live', 'polite');
    const n = this.products.length;
    count.textContent = n === 1 ? '1 saved item' : `${n} saved items`;
    bar.appendChild(count);

    const actions = document.createElement('div');
    actions.className = 'wishlist-grid__actions';

    const addable = this.products.filter((product) => this.#firstAvailableVariant(product));
    if (addable.length > 1) {
      const addAll = document.createElement('button');
      addAll.type = 'button';
      addAll.className = 'button';
      addAll.textContent = `Add all to cart (${addable.length})`;
      addAll.addEventListener('click', () => this.#addToCart(addAll, addable.map((product) => this.#firstAvailableVariant(product).id)));
      actions.appendChild(addAll);
    }

    if (this.dataset.share === 'true') {
      const share = document.createElement('a');
      share.className = 'wishlist-grid__share';
      share.href = this.#buildMailto();
      share.textContent = 'Share via email';
      actions.appendChild(share);
    }

    if (actions.childElementCount > 0) bar.appendChild(actions);
    return bar;
  }

  #buildMailto() {
    const handles = this.products.map((product) => product.handle).slice(0, SHARE_LIMIT);
    const shareUrl = new URL(withViewParam(window.location.pathname), window.location.origin);
    shareUrl.searchParams.set(SHARE_PARAM, handles.join(','));

    const lines = this.products.map((product) => {
      const url = new URL(product.url || `/products/${product.handle}`, window.location.origin);
      return `• ${product.title} — ${url.toString()}`;
    });
    const body = `Here's my Smooth Sales wish list:\n\n${lines.join('\n')}\n\nSee the whole list here:\n${shareUrl.toString()}`;
    const subject = this.dataset.shareSubject || 'My wish list';
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  #firstAvailableVariant(product) {
    if (product.snapshotOnly || !Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available) || null;
  }

  #buildCard(product) {
    const item = document.createElement('li');

    const card = document.createElement('div');
    card.className = 'wishlist-card';

    const media = document.createElement('a');
    media.className = 'wishlist-card__media';
    media.href = product.url || `/products/${product.handle}`;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');

    const imageSrc = product.featured_image || product.image;
    if (imageSrc) {
      const image = document.createElement('img');
      image.src = resizeImage(imageSrc, 480);
      image.alt = '';
      image.loading = 'lazy';
      image.width = 480;
      image.height = 480;
      media.appendChild(image);
    }

    const onSale =
      !product.snapshotOnly && typeof product.compare_at_price === 'number' && product.compare_at_price > product.price;
    const soldOut = !product.snapshotOnly && product.available === false;
    if (soldOut) {
      const badge = document.createElement('span');
      badge.className = 'wishlist-card__badge wishlist-card__badge--soldout';
      badge.textContent = 'Sold out';
      media.appendChild(badge);
    } else if (onSale) {
      const badge = document.createElement('span');
      badge.className = 'wishlist-card__badge';
      badge.textContent = 'Sale';
      media.appendChild(badge);
    }

    card.appendChild(media);
    card.appendChild(this.#buildHeart(product));

    const info = document.createElement('div');
    info.className = 'wishlist-card__info';

    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'wishlist-card__vendor';
      vendor.textContent = product.vendor;
      info.appendChild(vendor);
    }

    const title = document.createElement('a');
    title.className = 'wishlist-card__title';
    title.href = product.url || `/products/${product.handle}`;
    title.textContent = product.title || '';
    info.appendChild(title);

    if (product.snapshotOnly) {
      /* offline fallback: the price captured when the item was saved */
      const price = document.createElement('span');
      price.className = 'wishlist-card__price-note';
      price.textContent = product.price || '';
      info.appendChild(price);
    } else {
      info.appendChild(buildPrice(product, soldOut));
    }

    card.appendChild(info);

    const variant = this.#firstAvailableVariant(product);
    if (variant) {
      const atc = document.createElement('button');
      atc.type = 'button';
      atc.className = 'wishlist-card__atc';
      atc.textContent = 'Add to cart';
      atc.addEventListener('click', () => this.#addToCart(atc, [variant.id]));
      card.appendChild(atc);
    } else if (soldOut) {
      const atc = document.createElement('button');
      atc.type = 'button';
      atc.className = 'wishlist-card__atc wishlist-card__atc--soldout';
      atc.disabled = true;
      atc.textContent = 'Sold out';
      card.appendChild(atc);
    }

    item.appendChild(card);
    return item;
  }

  #buildHeart(product) {
    const heart = document.createElement('button');
    heart.type = 'button';
    heart.className = 'wishlist-card__heart';

    const shared = Boolean(this.sharedHandles);
    const render = () => {
      const saved = wishlistStore.has(product.handle);
      heart.innerHTML = shared ? (saved ? HEART_FILLED : HEART_OUTLINE) : HEART_FILLED;
      heart.setAttribute('aria-label', shared ? (saved ? 'Saved to your wish list' : 'Save to your wish list') : 'Remove from wish list');
    };
    render();

    heart.addEventListener('click', () => {
      if (shared) {
        wishlistStore.toggle({
          handle: product.handle,
          id: String(product.id || ''),
          title: product.title,
          url: product.url || `/products/${product.handle}`,
          price: typeof product.price === 'number' ? formatMoney(product.price) : String(product.price || ''),
          image: product.featured_image || product.image || '',
        });
        render();
      } else {
        wishlistStore.remove(product.handle);
      }
    });

    return heart;
  }

  async #addToCart(button, variantIds) {
    const label = button.textContent;
    button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'wishlist',
        lines: variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);

      const addUrl =
        typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : `${root}cart/add.js`;
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id, quantity: 1 })),
          sections: sectionIds.length > 0 ? [...new Set(sectionIds)].join(',') : undefined,
        }),
      });
      const addResult = await addResponse.json();
      if (addResult.status) throw new Error(addResult.message || 'Add to cart failed');

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: addResult.sections || undefined,
          source: 'wishlist',
          itemCount: variantIds.length,
          productId: '',
        },
      });

      button.textContent = 'Added ✓';
      window.setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 1600);
    } catch (error) {
      deferred.reject(error);
      button.textContent = label;
      button.disabled = false;
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
